import { useEffect } from "react";
import i18n from "../i18n";
import { useAuth } from "./useAuth";
import { analyticsAllowed, useAnalyticsConsent } from "./useAnalyticsConsent";

// Fire-and-forget pings to the public landing-events endpoint
// (platform_analytics). Failures are swallowed — analytics must never surface
// an error on the marketing page. First-party only (no third-party trackers):
// every event carries the visitor id + dwell/scroll/attribution signals.
//
// Default-on (opt-out): send() runs unless the visitor has explicitly opted out
// (analyticsAllowed()). To keep the first-party audience-measurement basis clean,
// the visitor id has a capped lifetime (see VISITOR_TTL_MS) rather than living
// forever.

const BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  "http://localhost:8000";
const ENDPOINT = `${BASE}/api/v1/platform/landing-events/`;

const VIEWED_KEY = "adam:landing_viewed";
const VISITOR_KEY = "adam:visitor_id";
const VISITOR_TS_KEY = "adam:visitor_id_ts";
const ATTR_KEY = "adam:landing_attr";

// Cap the visitor id lifetime at ~13 months (audience-measurement exemption):
// after this it is regenerated, so it is not a permanent identifier.
const VISITOR_TTL_MS = 390 * 24 * 60 * 60 * 1000;

type LandingEventName =
  | "landing.viewed"
  | "landing.cta_clicked"
  | "landing.section_dwell"
  | "landing.scroll_depth";

// Section element ids observed for dwell — must match the `id` on each landing
// <section>.
const TRACKED_SECTIONS = [
  "hero",
  "capabilities",
  "product-tour",
  "verticals",
  "pricing",
  "faq",
  "final-cta",
] as const;

const MIN_DWELL_MS = 1000; // ignore sub-second flickers past a section

function deviceClass(): "mobile" | "tablet" | "desktop" {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function visitorId(): string {
  try {
    const v = localStorage.getItem(VISITOR_KEY);
    const tsRaw = localStorage.getItem(VISITOR_TS_KEY);
    const ts = tsRaw ? Number(tsRaw) : 0;
    const fresh = v && ts && Date.now() - ts < VISITOR_TTL_MS;
    if (fresh) return v;
    // Mint (or rotate an expired id) and stamp the mint time so the id ages out
    // after VISITOR_TTL_MS rather than persisting forever.
    const next = crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
    localStorage.setItem(VISITOR_KEY, next);
    localStorage.setItem(VISITOR_TS_KEY, String(Date.now()));
    return next;
  } catch {
    return "";
  }
}

/** Capture entry attribution (UTM + referrer host + landing path) once per
 * session — later section/scroll events reuse it so a deep-scroll visit still
 * carries the channel that brought them. */
function captureAttribution(): Record<string, string> {
  try {
    const cached = sessionStorage.getItem(ATTR_KEY);
    if (cached) return JSON.parse(cached) as Record<string, string>;
  } catch {
    /* ignore */
  }
  const params = new URLSearchParams(window.location.search);
  let referrerHost = "";
  try {
    if (document.referrer) {
      const r = new URL(document.referrer);
      if (r.host && r.host !== window.location.host) {
        referrerHost = r.host.slice(0, 255);
      }
    }
  } catch {
    /* ignore malformed referrer */
  }
  const attr: Record<string, string> = {
    utm_source: params.get("utm_source") ?? "",
    utm_medium: params.get("utm_medium") ?? "",
    utm_campaign: params.get("utm_campaign") ?? "",
    utm_term: params.get("utm_term") ?? "",
    utm_content: params.get("utm_content") ?? "",
    referrer_host: referrerHost,
    landing_path: window.location.pathname.slice(0, 255),
  };
  for (const k of Object.keys(attr)) if (!attr[k]) delete attr[k];
  try {
    sessionStorage.setItem(ATTR_KEY, JSON.stringify(attr));
  } catch {
    /* ignore */
  }
  return attr;
}

function enrich(meta: Record<string, unknown>): Record<string, unknown> {
  const language = (i18n.language ?? "en").split("-")[0];
  return {
    language,
    visitor_id: visitorId(),
    device: deviceClass(),
    viewport_w: window.innerWidth,
    ...captureAttribution(),
    ...meta,
  };
}

function send(
  eventName: LandingEventName,
  meta: Record<string, unknown> = {},
  beacon = false
): void {
  if (!analyticsAllowed()) return; // opt-in gate — no consent, no tracking.
  const payload = JSON.stringify({ event_name: eventName, ...enrich(meta) });
  if (beacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(
        ENDPOINT,
        new Blob([payload], { type: "application/json" })
      );
      return;
    } catch {
      // fall through to fetch
    }
  }
  void fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => {
    // The page must work even if analytics is down or throttled.
  });
}

export function trackLandingCta(cta: string, section?: string): void {
  send("landing.cta_clicked", section ? { cta, section } : { cta });
}

/**
 * Attribution payload for registration (ADR 0022): the landing visitor_id
 * (only if still fresh — never re-minted here) plus the session-cached
 * UTM/referrer entry attribution. Returns null when the visitor opted out of
 * analytics or nothing was captured, so signup stays attribution-free.
 */
export function getSignupAttribution(): Record<string, string> | null {
  if (!analyticsAllowed()) return null;
  const attr: Record<string, string> = {};
  try {
    const cached = sessionStorage.getItem(ATTR_KEY);
    if (cached) Object.assign(attr, JSON.parse(cached) as Record<string, string>);
  } catch {
    /* ignore */
  }
  try {
    const v = localStorage.getItem(VISITOR_KEY);
    const tsRaw = localStorage.getItem(VISITOR_TS_KEY);
    const ts = tsRaw ? Number(tsRaw) : 0;
    if (v && ts && Date.now() - ts < VISITOR_TTL_MS) {
      attr.visitor_id = v;
    }
  } catch {
    /* ignore */
  }
  for (const k of Object.keys(attr)) if (!attr[k]) delete attr[k];
  return Object.keys(attr).length ? attr : null;
}

/** Records one `landing.viewed` event per browser session, only for resolved
 * guests (an authenticated user passing through `/` is not a landing visit). */
export function useLandingPageView(): void {
  const { isAuthenticated, isLoading } = useAuth();
  const { consent } = useAnalyticsConsent();

  useEffect(() => {
    if (isLoading || isAuthenticated || consent === "denied") return;
    try {
      if (sessionStorage.getItem(VIEWED_KEY) === "1") return;
      sessionStorage.setItem(VIEWED_KEY, "1");
    } catch {
      // sessionStorage unavailable (private mode) — record the view anyway.
    }
    send("landing.viewed", { section: "page" });
  }, [isLoading, isAuthenticated, consent]);
}

/** Rich engagement instrumentation: per-section dwell (IntersectionObserver),
 * max scroll depth, flushed on tab-hide via sendBeacon. Active for resolved
 * guests. */
export function useLandingInstrumentation(): void {
  const { isAuthenticated, isLoading } = useAuth();
  const { consent } = useAnalyticsConsent();

  useEffect(() => {
    if (isLoading || isAuthenticated || consent === "denied") return;

    // ── Section dwell ──────────────────────────────────────────────────────
    const dwell = new Map<string, { enteredAt: number | null; total: number }>();
    const observed: HTMLElement[] = [];
    for (const id of TRACKED_SECTIONS) {
      const el = document.getElementById(id);
      if (el) {
        observed.push(el);
        dwell.set(id, { enteredAt: null, total: 0 });
      }
    }

    const flushSection = (id: string, beacon = false) => {
      const d = dwell.get(id);
      if (!d) return;
      if (d.enteredAt != null) {
        d.total += performance.now() - d.enteredAt;
        d.enteredAt = null;
      }
      if (d.total >= MIN_DWELL_MS) {
        send(
          "landing.section_dwell",
          { section: id, dwell_ms: Math.round(d.total) },
          beacon
        );
        d.total = 0;
      }
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const id = (e.target as HTMLElement).id;
          const d = dwell.get(id);
          if (!d) continue;
          if (e.isIntersecting) {
            d.enteredAt = performance.now();
          } else {
            flushSection(id);
          }
        }
      },
      { threshold: 0.5 }
    );
    observed.forEach((el) => io.observe(el));

    // ── Scroll depth ───────────────────────────────────────────────────────
    let maxScroll = 0;
    const onScroll = () => {
      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      const pct =
        scrollable > 0 ? Math.round((window.scrollY / scrollable) * 100) : 100;
      if (pct > maxScroll) maxScroll = Math.min(100, pct);
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // ── Flush on tab hide / unload ─────────────────────────────────────────
    const flushAll = () => {
      for (const id of dwell.keys()) flushSection(id, true);
      if (maxScroll > 0) {
        send("landing.scroll_depth", { scroll_pct: maxScroll }, true);
        maxScroll = 0;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flushAll();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flushAll);

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flushAll);
      flushAll();
    };
  }, [isLoading, isAuthenticated, consent]);
}
